import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { RMCDashboard as RMCDashboardType } from "../service/rmcDashboardService";
import { rmcDashboardService } from "../service/rmcDashboardService";
import "./AdminDashboard.enterprise.css"; // Use enterprise styles
import PageHeader from "./PageHeader";
import SEO from "./SEO";

const RMCAdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<RMCDashboardType | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [rmcId, setRmcId] = useState<number | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [showCharts, setShowCharts] = useState(false);

  // Cache for 5 minutes to avoid repeated API calls
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  const navigate = useNavigate();

  const userFullName =
    useSelector((state: any) => state.auth.userFullName) || "RMC Administrator";
  const userRole = useSelector((state: any) => state.auth.userRole) || "";
  const userRMCId = useSelector((state: any) => state.auth.rmcId) || null;

  // Professional enterprise color palette
  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4"];

  const fetchDashboardData = async (
    currentRmcId: number,
    forceRefresh: boolean = false
  ) => {
    // Check cache first
    const now = Date.now();
    if (
      !forceRefresh &&
      dashboardData &&
      now - lastFetchTime < CACHE_DURATION
    ) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      setError("");
      const data = await rmcDashboardService.getRMCDashboard(currentRmcId);
      setDashboardData(data);
      setLastFetchTime(now);
      // Enable charts after data is loaded
      setTimeout(() => setShowCharts(true), 100);
    } catch (err: any) {
      setError(err.message || "Failed to load RMC dashboard data");
      console.error("RMC Dashboard error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load - get RMC ID from user context or props
  useEffect(() => {
    const currentRmcId = userRMCId || 1; // Default to 1 if no RMC ID in user context
    setRmcId(currentRmcId);
    fetchDashboardData(currentRmcId);
  }, [userRMCId]);

  // Manual refresh handler
  const handleRefresh = () => {
    if (rmcId) {
      setRefreshing(true);
      fetchDashboardData(rmcId, true); // Force refresh
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString();

  // Map an activity icon name (string from API) to a react-bootstrap-icons component
  const getIconComponent = (iconName: string) => {
    const activityIconMap: { [key: string]: React.ReactNode } = {
      building: <Icon.Building />,
      user: <Icon.PersonCircle />,
      client: <Icon.People />,
      property: <Icon.HouseDoor />,
      room: <Icon.DoorOpen />,
      booking: <Icon.CalendarCheck />,
      proposal: <Icon.FileEarmarkText />,
      create: <Icon.PlusCircle />,
      update: <Icon.PencilSquare />,
      delete: <Icon.Trash />,
    };
    return activityIconMap[iconName?.toLowerCase()] || <Icon.Activity />;
  };

  // Navigation handlers for dashboard tiles
  const handleClientsClick = () => navigate("/clients");
  const handleProposalsClick = () => navigate("/rmc/proposal-management");
  const handleUsersClick = () => navigate("/users");
  const handleBookingsClick = () => navigate("/rmc/bookings");

  if (loading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center text-center"
        style={{ height: "55vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <div className="mt-3 fw-semibold text-muted">
          Loading RMC dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Icon.ExclamationTriangle className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { stats, recentActivities, topClients, monthlyStats, rmcInfo } =
    dashboardData;

  // Ensure arrays are properly initialized
  const safeMonthlyStats = Array.isArray(monthlyStats) ? monthlyStats : [];
  const safeRecentActivities = Array.isArray(recentActivities)
    ? recentActivities
    : [];
  const safeTopClients = Array.isArray(topClients) ? topClients : [];

  return (
    <div className="content">
      <SEO
        title="RMC Dashboard"
        noIndex
        description="RMC administrative dashboard"
      />
      <PageHeader
        title={`RMC Dashboard - ${rmcInfo?.name || "Dashboard"}`}
        showBackButton={false}
        extra={
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handleRefresh}
              disabled={refreshing}
              className="d-flex align-items-center"
            >
              <Icon.ArrowClockwise
                className={`me-1 ${refreshing ? "spin" : ""}`}
              />
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </div>
        }
      />
      <div className="content-body">
        <div className="container" style={{ maxWidth: "1400px" }}>
          <div className="d-flex flex-column flex-sm-row align-items-sm-end align-items-start justify-content-between mb-3">
            <div>
              <p className="text-muted mb-1">Welcome back, {userFullName}!</p>
              <h5
                className="fw-semibold mb-0"
                style={{ letterSpacing: ".5px" }}
              >
                {rmcInfo?.name} - {rmcInfo?.cityName} Overview
              </h5>
            </div>
          </div>

          {/* Main Stats Cards */}
          <Row className="mb-4 g-3">
            <Col xl={3} lg={6} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleClientsClick}
                title="Click to view Clients"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-primary text-white me-3">
                    <Icon.People size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-primary">{stats.totalClients}</h4>
                    <div className="soft-muted">Total Clients</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={6} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleProposalsClick}
                title="Click to view Proposals"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-success text-white me-3">
                    <Icon.FileEarmarkText size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-success">
                      {stats.totalProposals}
                    </h4>
                    <div className="soft-muted">Total Proposals</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={6} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleUsersClick}
                title="Click to view All Users"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-info text-white me-3">
                    <Icon.PersonBadge size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-info">{stats.totalUsers}</h4>
                    <div className="soft-muted">Total Users</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={3} lg={6} md={6} className="mb-3">
              <Card
                className="stats-card h-100"
                style={{ cursor: "pointer" }}
                onClick={handleBookingsClick}
                title="Click to view Bookings"
              >
                <Card.Body className="d-flex align-items-center">
                  <div className="stats-icon bg-danger text-white me-3">
                    <Icon.CalendarCheck size={24} />
                  </div>
                  <div>
                    <h4 className="mb-1 text-danger">{stats.totalBookings}</h4>
                    <div className="soft-muted">Total Bookings</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="mb-4 g-4">
            {/* Monthly Growth Trends */}
            <Col lg={8} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.GraphUp className="me-2" />
                    Monthly Growth Trends
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={safeMonthlyStats.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="newClients"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        name="New Clients"
                      />
                      <Line
                        type="monotone"
                        dataKey="newProposals"
                        stroke="#10b981"
                        strokeWidth={2}
                        name="New Proposals"
                      />
                      <Line
                        type="monotone"
                        dataKey="newBookings"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        name="New Bookings"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            {/* RMC Overview Pie Chart */}
            <Col lg={4} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.PieChart className="me-2" />
                    RMC Overview
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Users",
                            value: stats.totalUsers,
                            color: "#3b82f6",
                          },
                          {
                            name: "Clients",
                            value: stats.totalClients,
                            color: "#10b981",
                          },
                          {
                            name: "Proposals",
                            value: stats.totalProposals,
                            color: "#f59e0b",
                          },
                          {
                            name: "Bookings",
                            value: stats.totalBookings,
                            color: "#ef4444",
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label
                      >
                        {[
                          {
                            name: "Users",
                            value: stats.totalUsers,
                            color: "#3b82f6",
                          },
                          {
                            name: "Clients",
                            value: stats.totalClients,
                            color: "#10b981",
                          },
                          {
                            name: "Proposals",
                            value: stats.totalProposals,
                            color: "#f59e0b",
                          },
                          {
                            name: "Bookings",
                            value: stats.totalBookings,
                            color: "#ef4444",
                          },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Revenue and Performance Charts */}
          <Row className="mb-4 g-4">
            {/* Monthly Revenue Chart */}
            <Col lg={6} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.CurrencyDollar className="me-2" />
                    Monthly Revenue
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={safeMonthlyStats.slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            {/* Top Clients Performance Bar Chart */}
            <Col lg={6} className="mb-3">
              <Card className="chart-card h-100 gradient">
                <Card.Header className="widget-header">
                  <h5 className="mb-0">
                    <Icon.BarChart className="me-2" />
                    Top Clients Performance
                  </h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={safeTopClients.slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="totalProposals"
                        fill="#3b82f6"
                        name="Proposals"
                      />
                      <Bar
                        dataKey="activeBookings"
                        fill="#10b981"
                        name="Bookings"
                      />
                      <Bar
                        dataKey="totalRevenue"
                        fill="#f59e0b"
                        name="Revenue"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Details Row */}
          <Row className="mb-4 g-4">
            {/* Recent Activities */}
            <Col lg={6} className="mb-3">
              <Card className="h-100 chart-card gradient">
                <Card.Header className="widget-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <Icon.Clock className="me-2" />
                    Recent Activities
                  </h5>
                  <Badge bg="primary">{safeRecentActivities.length}</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="activity-list">
                    {safeRecentActivities.map((activity) => (
                      <div
                        key={activity.id}
                        className="activity-item p-3 border-0"
                      >
                        <span className="timeline-dot" />
                        <div className="d-flex align-items-start">
                          <div
                            className={`activity-icon me-3 text-${activity.color}`}
                          >
                            {getIconComponent(activity.icon)}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                              <div
                                className="fw-semibold"
                                style={{ fontSize: ".85rem" }}
                              >
                                {activity.action} {activity.type.toLowerCase()}
                              </div>
                              <small
                                className="text-muted"
                                style={{ fontSize: ".7rem" }}
                              >
                                {formatDate(activity.createdDate)}
                              </small>
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: ".7rem" }}
                            >
                              {activity.entityName} by {activity.userName}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Top Clients */}
            <Col lg={6} className="mb-3">
              <Card className="h-100 chart-card gradient">
                <Card.Header className="widget-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <Icon.Trophy className="me-2" />
                    Top Performing Clients
                  </h5>
                  <Badge bg="success">{safeTopClients.length}</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <table className="table table-sm mb-0 top-rmc-table">
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Proposals</th>
                          <th>Bookings</th>
                          <th>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safeTopClients.map((client) => {
                          const activityPct = client.totalProposals
                            ? Math.min(
                                100,
                                Math.round(
                                  (client.activeBookings /
                                    (client.totalProposals || 1)) *
                                    100
                                )
                              )
                            : 0;
                          return (
                            <tr key={client.id}>
                              <td style={{ minWidth: 160 }}>
                                <div
                                  className="fw-semibold"
                                  style={{ fontSize: ".8rem" }}
                                >
                                  {client.name}
                                </div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: ".65rem" }}
                                >
                                  {client.contactEmail}
                                </div>
                                <div className="progress-thin mt-1">
                                  <span style={{ width: activityPct + "%" }} />
                                </div>
                              </td>
                              <td>
                                <span className="metric-badge clients">
                                  <Icon.FileEarmarkText size={10} />{" "}
                                  {client.totalProposals}
                                </span>
                              </td>
                              <td>
                                <span className="metric-badge bookings">
                                  <Icon.Calendar2CheckFill size={10} />{" "}
                                  {client.activeBookings}
                                </span>
                              </td>
                              <td>
                                <span className="metric-badge revenue">
                                  <Icon.CurrencyDollar size={10} />{" "}
                                  {formatCurrency(client.totalRevenue)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default RMCAdminDashboard;
